<template lang="pug">
  .wrap
    page-title.report-title(fontSize='30px' :wrapStyle='{ marginBottom: "10px" }')
      | Your survey report:

    .brief-title

    time-switcher(v-model='dateRange')

    .overall-stats(v-if='overallStats' :key='loadingOverallStats ? "forceRefresh" : "done"')
      .stat-panel.count
        .num {{ secondsToMinuteSeconds(overallStats.counts.avgCompletionTime) }}
        .label Average time to complete

      .stat-panel.count
        .num {{ overallStats.counts.totUsersAssigned }}
        .label Total users assigned

      .stat-panel.count
        .num {{ overallStats.counts.totalTarget }}
        .label Total target

      .stat-panel
        circular-counter(id='completed' label='Completed' :total='overallStats.counts.totalCompleted' :count='overallStats.counts.periodCompleted'
          color='#f5a722')

    .tabs
      .tab(:class='{ active: activeTab === "report" }' @click='switchTab("report")') Report
      .tab(:class='{ active: activeTab === "results" }' @click='switchTab("results")') Results
      .tab(:class='{ active: activeTab === "users" }' @click='switchTab("users")') Users

    .tab-content(v-if='activeTab === "report"')
      .view-switch
        h2 VIEW AS
        .view-switch-btn(:class='{ active: activeView === "list" }' @click='switchView("list")')
          icon(name='list' color='#000' :scale='1.3')
        .view-switch-btn(:class='{ active: activeView === "pie" }' @click='switchView("pie")')
          icon(name='pie-chart' color='#000' :scale='1.3')

      .questions(v-if='questionStats' :class='{pie: activeView === "pie"}')
        .question(v-for='question in questionData' :key='question.id' )
          .question-title {{ question.title }}

          .answer-list(v-if='activeView === "list"')
            template(v-if='question.type === "short_text"')
              template(v-if='question.answers.length')
                | {{ question.answers.length }} answer(s) -
                a(href='' @click.prevent='selectQuestion(question)') click here to see results
            template(v-else)
              .answer(v-for='(answer, answerIndex) in question.answers' :key='answerIndex')
                .answer-value {{ formatAnswerValue(answer.value) }}
                .answer-progress
                  .progress
                    .progress-bar(:style="{ width: `${answer.percentage}%`, backgroundColor: answer.bgColor }")
                  .answer-info
                    .answer-total {{answer.tot}} /
                    .percentage {{calculatePercentage(answer.tot, question.totAnswers)}}%

          .answer-pie(v-if='activeView === "pie"')
            template(v-if='question.type === "short_text"')
              template(v-if='question.answers.length')
                | {{ question.answers.length }} answer(s) -
                a(href='' @click.prevent='selectQuestion(question)') click here to see results
            template(v-else)
              .legends
                .legend(v-for="(item, index) in question.legend" :key="index")
                  span(:style="{ borderColor: `${item.colour}` }")
                  p {{ formatAnswerValue(item.label) }} ({{item.percentage}}%)

              doughnut-chart(v-if="question.answers.length" :chart-data="question.pieChartData"
                            :options='doughnutChartOptions' :height='300')

            div(v-else) No answers yet.

    .tab-content(v-if='activeTab === "results"')
      a(:href="resultsCsvUrl")
        el-button(:style='{ marginBottom: "10px" }') Export as CSV
      el-table.result-table(:data="pivotAnswers.rows" empty-text="No entries" v-loading='loadingAnswers')
        el-table-column(v-for='(question, colIndex) in pivotAnswers.headers' :key='colIndex' align='center' :render-header="renderTooltip" min-width="200")
          template(slot-scope="scope")
            template(v-if='scope.row[colIndex]')
              .answer-list-item(v-for='answer in scope.row[colIndex]') {{ formatAnswerValue(answer.value) }}
            template(v-else) -


    .tab-content(v-if='activeTab === "users"')
      el-row.punchcard-actions(v-if='punchCards')
        el-col.punchcard-filter(:span='10')
          el-input(placeholder="Search users..." v-model='searchParams.q' @keyup.native.enter="searchUsers" :on-icon-click='searchUsers' icon="search")

      .el-table.el-table--fit.el-table--border(v-if='punchCards && overallStats' v-loading='loadingPunchCards')
        .el-table__body-wrapper
          table.el-table__body.table(cellpadding='0' cellspacing='0' border='0')
            tbody
              tr.el-table__row
                th(:style='{ width: "200px" }')
                  .cell User

                th.is-center.days(v-for='punchDate in punchDates' v-if='shouldShowWeekdays' :style='{ width: "60px" }')
                  .cell {{ formatPunchCardDate(punchDate) }}

                th.is-center.stat-cell(:style='{ width: "90px" }')
                  .cell Target

              tr.el-table__row(v-for='entry in punchCards.punchcards' v-if='shouldShowWeekdays' :key='entry.id')
                td
                  .cell.user-cell
                    .name {{ entry.firstName }} {{ entry.lastName }}
                    .email {{ entry.email }}

                td.is-center(v-for='punchDate in entry.dates' :style='{ textAlign: "center" }')
                  .cell {{ punchDate.responseCount ? punchDate.responseCount : '' }}

                td.is-center
                  | {{ formatPercentage(entry.totResponses, overallStats.counts.totalTarget) }}


      div(v-else)
        | This survey has not been assigned to anyone yet.

    el-dialog(v-if='selectedQuestion' :visible="showFreeTextModal" :before-close='onFreeTextModalClose')
      template(slot='title') {{ selectedQuestion.title }}

      .user-answer(v-for='answer in selectedQuestion.answers' :key='answer.id')
        | {{ answer.value }}

</template>

<script>
import _ from "lodash";
import format from "date-fns/format";
import startOfWeek from "date-fns/start_of_week";
import endOfWeek from "date-fns/end_of_week";
import eachDay from "date-fns/each_day";
import differenceInDays from "date-fns/difference_in_days";

import { mapActions, mapGetters } from "vuex";
import PageTitle from "@/shared/components/layout/PageTitle";
import TimeSwitcher from "@/brapp/components/shared/TimeSwitcher";
import CircularCounter from "@/shared/components/CircularCounter";
import DoughnutChart from "./DoughnutChart";

const formatApiDate = date => format(date, "YYYY-MM-DD");
const padTime = value => _.padStart(value, 2, "0");

const VALUES_MAP = {
  True: "Yes",
  False: "No"
};

export default {
  components: { PageTitle, TimeSwitcher, CircularCounter, DoughnutChart },
  data() {
    const now = Date.now();
    const dateRange = [
      formatApiDate(startOfWeek(now, { weekStartsOn: 1 })),
      formatApiDate(endOfWeek(now, { weekStartsOn: 1 }))
    ];
    return {
      activeTab: "report",
      activeView: "list",
      searchParams: {
        q: ""
      },
      questionStats: null,
      overallStats: null,
      punchCards: null,
      loadingAnswers: false,
      loadingPunchCards: false,
      answers: null,
      loadingOverallStats: false,
      mode: "weekly",
      dateRange,
      pieColours: [
        "#f45943",
        "#74c6c0",
        "#5d79b5",
        "#f8a523",
        "#1abc9c",
        "#16a085",
        "#2ecc71",
        "#27ae60",
        "#3498db",
        "#2980b9",
        "#9b59b6",
        "#8e44ad",
        "#34495e",
        "#2c3e50",
        "#f1c40f",
        "#f39c12",
        "#e67e22",
        "#d35400",
        "#e74c3c",
        "#c0392b",
        "#ecf0f1",
        "#bdc3c7",
        "#95a5a6",
        "#7f8c8d"
      ],
      doughnutChartOptions: {
        responsive: true,
        legend: {
          display: false
        },
        layout: {
          padding: 20
        }
      },
      selectedQuestion: null
    };
  },
  computed: {
    ...mapGetters("api", ["generateUrl"]),
    ...mapGetters("auth", ["activeCompany"]),
    surveyId() {
      return this.$route.params.surveyId;
    },
    pivotAnswers() {
      if (!this.answers || !this.answers.length) {
        return {
          headers: [],
          rows: []
        };
      }
      const grouped = _.map(
        _.values(_.groupBy(this.answers, "submissionToken")),
        group =>
          _.orderBy(
            _.values(_.groupBy(group, "typeformFieldId")),
            "question.id"
          )
      );

      return {
        headers: _.map(_.head(grouped), group => group[0].question),
        rows: grouped
      };
    },
    resultsCsvUrl() {
      const params = {
        format: "csv",
        submitted_at_after: formatApiDate(this.dateRange[0]),
        submitted_at_before: formatApiDate(this.dateRange[1]),
        question__survey: this.surveyId
      };
      return this.generateUrl({ path: "/insight/survey-answers/", params });
    },
    questionData() {
      if (!this.questionStats || !this.questionStats.questions.length) {
        return [];
      }
      return this.questionStats.questions.map(q => {
        const totals = q.answers.map(q2 => q2.tot);
        const maxTotal = _.max(totals);
        const totalsEquallySplit = _.uniq(totals).length === 1;

        return {
          ...q,
          answers: q.answers.map(a => ({
            ...a,
            percentage: this.calculatePercentage(a.tot, q.totAnswers),
            bgColor:
              q.answers.length > 1 && !totalsEquallySplit && maxTotal === a.tot
                ? "#71c8bf"
                : undefined
          })),
          legend: q.answers.map((a, answerIndex) => {
            const r = {
              label: a.value,
              colour: this.pieColours[answerIndex],
              percentage: this.calculatePercentage(a.tot, q.totAnswers)
            };
            return r;
          }),
          pieChartData: {
            datasets: [
              {
                data: q.answers.map(a => a.tot),
                backgroundColor: this.pieColours
              }
            ],
            labels: q.answers.map(a => a.value)
          }
        };
      });
    },
    shouldShowWeekdays() {
      if (!this.dateRange) {
        return false;
      }
      return differenceInDays(this.dateRange[1], this.dateRange[0]) <= 14;
    },
    punchDates() {
      return eachDay(this.dateRange[0], this.dateRange[1]);
    },
    showFreeTextModal() {
      return !!this.selectedQuestion;
    }
  },
  watch: {
    dateRange(newVal) {
      this.onRangeChange(newVal);
    },
    activeTab(newVal) {
      if (newVal === "results") {
        this.loadAnswerList();
      } else if (newVal === "users") {
        this.loadPunchCards();
      }
    }
  },
  methods: {
    ...mapActions("surveys", [
      "fetchAnswersReport",
      "fetchOverallReport",
      "fetchAnswers",
      "fetchPunchCards"
    ]),
    loadReports() {
      const params = {
        start: formatApiDate(this.dateRange[0]),
        end: formatApiDate(this.dateRange[1])
      };
      this.loadingOverallStats = true;
      this.fetchOverallReport({ id: this.surveyId, params }).then(resp => {
        this.loadingOverallStats = false;
        this.overallStats = resp;
      });

      this.fetchAnswersReport({ id: this.surveyId, params }).then(resp => {
        this.questionStats = resp;
      });
    },
    loadAnswerList() {
      const params = {
        submittedAtAfter: formatApiDate(this.dateRange[0]),
        submittedAtBefore: formatApiDate(this.dateRange[1])
      };
      this.loadingAnswers = true;
      this.fetchAnswers({
        params: { ...params, question__survey: this.surveyId }
      }).then(resp => {
        this.answers = resp;
        this.loadingAnswers = false;
      });
    },
    loadPunchCards() {
      const params = {
        start: formatApiDate(this.dateRange[0]),
        end: formatApiDate(this.dateRange[1]),
        ...this.searchParams
      };
      this.loadingPunchCards = true;
      this.fetchPunchCards({ id: this.surveyId, params }).then(resp => {
        this.punchCards = resp;
        this.loadingPunchCards = false;
      });
    },
    secondsToMinuteSeconds(d) {
      if (!d) {
        return "00:00";
      }
      const minutes = Math.floor((d % 3600) / 60);
      const seconds = Math.floor((d % 3600) % 60);
      return `${padTime(minutes)}:${padTime(seconds)}`;
    },
    onRangeChange() {
      this.loadReports();
      if (this.activeTab === "results") {
        this.loadAnswerList();
      } else if (this.activeTab === "users") {
        this.loadPunchCards();
      }
    },
    calculatePercentage(num, tot) {
      return tot ? ((num / tot) * 100).toFixed(2).replace(/[.,]00$/, "") : 0;
    },
    switchTab(tabName) {
      this.activeTab = tabName;
    },
    switchView(viewName) {
      this.activeView = viewName;
    },
    searchUsers() {
      this.loadPunchCards();
    },
    renderTooltip(h, { $index }) {
      const question = this.pivotAnswers.headers[$index];
      return h(
        "el-tooltip",
        {
          attrs: {
            content: question.title,
            placement: "top",
            popperClass: "ethos-tooltip"
          }
        },
        [h("span", {}, [question.title])]
      );
    },
    formatAnswerValue(answer) {
      return VALUES_MAP[answer] || answer;
    },
    formatPunchCardDate(date) {
      return format(date, "ddd")[0];
    },
    formatPercentage(num, tot) {
      return `${tot ? Math.floor((num / tot) * 100) : 0}%`;
    },
    onFreeTextModalClose() {
      this.selectedQuestion = null;
    },
    selectQuestion(question) {
      this.selectedQuestion = question;
    }
  },
  created() {
    this.loadReports();
  }
};
</script>

<style scoped lang="scss">
@import "../../../../../assets/scss/mixins.scss";

.stat-cell .cell {
  word-break: normal;
  line-height: 15px;
  padding: 10px 0;
}

.overall-stats {
  display: flex;
  width: 100%;
  justify-content: space-between;
  margin-bottom: 30px;

  .stat-panel {
    flex-basis: calc(25% - 10px);
    background: white;
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;

    .num {
      font-size: 40px;
      margin-bottom: 5px;
    }

    .label {
      font-size: 12px;
    }
  }
  @include MQ(M) {
    flex-wrap: wrap;
    .stat-panel {
      flex-basis: calc(50% - 10px);
      margin-bottom: 20px;
    }
  }
  @include MQ(S) {
    flex-direction: column;
    justify-content: flex-start;
    .stat-panel {
      flex-basis: auto;
      margin-bottom: 10px;
    }
  }
}

.brief-title {
  letter-spacing: 0.05em;
  font-size: 28px;
  font-family: "gilroylight";
  font-weight: normal;
  margin: 0 0 10px 0;
}
.tab-content {
  background-color: white;
  padding: 20px;
}

.questions {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  .question {
    margin-bottom: 20px;
    padding-bottom: 10px;
    background: white;
    padding: 10px;
    flex-basis: calc(50% - 20px);
  }
  @include MQ(ML) {
    flex-direction: column;
    .question {
      flex-basis: auto;
      margin-bottom: 30px;
      padding: 0;
    }
  }
}

.question-title {
  font-size: 20px;
  font-weight: 500;
  padding-bottom: 15px;
}

.answer {
  margin-bottom: 10px;
}

.answer-progress {
  display: flex;
  align-items: center;
  justify-content: stretch;
  border: 1px solid #eee;
  border-radius: 3px;
  margin-bottom: 15px;
  .index {
    flex-basis: 30px;
    text-align: center;
    font-size: 0.9em;
    color: #555;
  }
  .answer-info {
    flex-basis: 80px;
    min-width: 80px;
    padding-right: 5px;
    text-align: center;
    display: flex;
    justify-content: flex-end;
    align-items: center;
    color: #aaa;
    .percentage {
      color: #888;
      font-weight: bold;
      margin-left: 5px;
    }
  }
  .progress {
    flex-basis: calc(100% - 85px);
    height: 24px;
    position: relative;
    .progress-bar {
      height: 24px;
      position: absolute;
      left: 0;
      top: 0;
      background-color: #8c9da9;
    }
  }
}

.answer-value {
  font-size: 1em;
  margin: 0 0 5px 0px;
}

.answer-list-item {
  display: inline-block;
  margin-right: 5px;

  &:after {
    content: ", ";
  }

  &:last-child:after {
    content: "";
  }
}

.user-answer {
  padding: 0 10px;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  border-left: 2px solid #ddd;
}

.tabs {
  display: flex;
  align-items: center;
  margin: 10px 0;
}

.tab {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
  background: white;
  margin-right: 10px;
  cursor: pointer;

  &.active {
    background: #ddd;
  }
}

.answer-pie {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.view-switch {
  display: flex;
  margin-bottom: 20px;
  justify-content: flex-end;
  align-items: center;
  h2 {
    font-size: 1.2em;
    font-weight: bold;
  }
}

.view-switch-btn {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 10px;
  background: white;
  border: 1px solid #637279;
  margin: 0 0 0 10px;
  cursor: pointer;
  svg {
    color: #637279;
  }
  &.active {
    background: #637279;
    svg {
      fill: #fff;
    }
  }
}

.stat-cell .cell {
  word-break: normal;
  line-height: 15px;
  padding: 10px 0;
}

.table {
  width: 100%;
}

.user-cell {
  padding: 15px;

  .email {
    font-size: 13px;
    color: #999;
    margin-top: -8px;
  }
}

.name {
  font-size: 15px;
  margin-bottom: 5px;
}

.email {
  font-size: 13px;
}

.punchcard-actions {
  margin-bottom: 20px;
}
</style>
